import React from 'react';
import type { EntryPointProps } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import {
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalTitle,
	type KeyboardOrMouseEvent,
} from '@atlaskit/modal-dialog';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	MODAL,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import { BACKLOG } from '@atlassian/jira-software-resource-invalidator/src/common/types.tsx';
import { triggerResourceCacheInvalidation } from '@atlassian/jira-software-resource-invalidator/src/controllers/resources-cache-invalidation-subject/index.tsx';
import { DeleteSprintType } from '../../../model/modals/types';
import { useBacklogDispatch, useBacklogSelector } from '../../../state';
import { closeModal } from '../../../state/actions/modals';
import { sprintDeleteRequest } from '../../../state/actions/sprints/delete';
import { getCardLists, makeGetNextCardListId } from '../../../state/selectors/cards';
import { getOpenModalData } from '../../../state/selectors/modals';
import messages from './messages';

type Props = {
	sprintId: string;
	onClose: () => void;
};

function SprintDeleteModal(props: Props) {
	const { formatMessage } = useIntl();
	const { sprint, nextCardListId } = useBacklogSelector((state) => {
		let sprintId = props.sprintId;

		if (!fg('convert_sprint_delete_modal_to_entrypoint')) {
			const modalData = getOpenModalData(state);
			if (!modalData || modalData.type !== DeleteSprintType) {
				return {};
			}

			const { payload } = modalData;
			sprintId = payload.sprintId;
		}

		return {
			nextCardListId: makeGetNextCardListId(state)(String(sprintId)),
			sprint: sprintId ? getCardLists(state)[String(sprintId)] : null,
		};
	});

	const dispatch = useBacklogDispatch();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/*
      Should only need to create this once since the modal is closed
      by either confirm or cancel
    */
	const analyticsEvent = createAnalyticsEvent({
		actionSubject: 'button',
		action: 'clicked',
	});

	const onDelete = (e: KeyboardOrMouseEvent, onDeleteAnalyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'confirmButton');
		sprint &&
			dispatch(sprintDeleteRequest(sprint.id, nextCardListId ?? null, onDeleteAnalyticsEvent));

		// Since the sprint modals don't go through redux we need to fire their invalidations manually
		triggerResourceCacheInvalidation(BACKLOG, 'DELETE_SPRINT_MODAL_DELETE');

		!fg('convert_sprint_delete_modal_to_entrypoint') && dispatch(closeModal());
	};

	const onCloseModal = () => {
		fireUIAnalytics(analyticsEvent, 'cancelButton');
		dispatch(closeModal());
	};

	// handled in parent component itself
	if (!fg('convert_sprint_delete_modal_to_entrypoint') && !sprint) {
		return null;
	}

	const onClose = fg('convert_sprint_delete_modal_to_entrypoint') ? props.onClose : onCloseModal;

	return (
		<ShortcutScope>
			<ModalDialog width={400} onClose={onClose}>
				<ModalHeader>
					<ModalTitle appearance="danger">{formatMessage(messages.title)}</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<p>
						<FormattedMessage
							{...messages.areYouSure}
							values={{
								sprintName: <b>{sprint?.name}</b>,
							}}
						/>
					</p>
					<ViewTracker />
				</ModalBody>
				<ModalFooter>
					<Button appearance="subtle" onClick={onClose}>
						{formatMessage(messages.cancel)}
					</Button>
					<Button appearance="danger" onClick={onDelete}>
						{formatMessage(messages.delete)}
					</Button>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
}

export default function SprintDeleteModalWithAnalytics({
	props,
}: EntryPointProps<{}, {}, Props, {}>) {
	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="deleteSprint">
			<SprintDeleteModal {...props} />
		</ContextualAnalyticsData>
	);
}
